import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail, updateProfile } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuthStore } from '../store/authStore';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuthStore();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [editName, setEditName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground">You are not logged in.</p>
        <button
          onClick={() => navigate('/login')}
          className="mt-4 px-6 py-2 bg-primary text-primary-foreground rounded-lg"
        >
          Go to Login
        </button>
      </div>
    );
  }

  const handleChangePassword = async () => {
    setMessage('');
    setError('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, user.email);
      setMessage('Password reset email sent. Check your inbox.');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSaveName = async () => {
    if (!nameValue.trim()) return;
    setSavingName(true);
    setError('');
    setMessage('');
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        await updateProfile(currentUser, { displayName: nameValue.trim() });
      }
      setUser({ ...user, display_name: nameValue.trim() });
      // Update localStorage admin users registry
      try {
        const registry = JSON.parse(localStorage.getItem('pmp_admin_users_registry') || '[]');
        const idx = registry.findIndex((u: any) => u.id === user.id);
        if (idx >= 0) {
          registry[idx].display_name = nameValue.trim();
          localStorage.setItem('pmp_admin_users_registry', JSON.stringify(registry));
        }
      } catch { /* ignore */ }
      setEditName(false);
      setMessage('Name updated successfully.');
    } catch (err: any) {
      setError(err.message || 'Failed to update name.');
    } finally {
      setSavingName(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="bg-card border rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b">
          <h1 className="text-2xl font-bold">User Profile</h1>
          <p className="text-sm text-muted-foreground">Manage your account details</p>
        </div>

        <div className="p-6 space-y-6">
          {message && (
            <div className="p-3 text-sm bg-green-100 text-green-700 rounded-lg">{message}</div>
          )}
          {error && (
            <div className="p-3 text-sm bg-destructive/10 text-destructive rounded-lg">{error}</div>
          )}

          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold">
              {(user.display_name || user.email || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              {!editName ? (
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-lg font-semibold">{user.display_name || 'No display name'}</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                  <button
                    onClick={() => { setEditName(true); setNameValue(user.display_name || ''); }}
                    className="px-3 py-1.5 text-sm border rounded-lg hover:bg-accent whitespace-nowrap"
                  >
                    Edit
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    placeholder="Your name"
                    className="flex-1 px-4 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveName(); }}
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={savingName || !nameValue.trim()}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
                  >
                    {savingName ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditName(false)}
                    className="px-4 py-2 border rounded-lg hover:bg-accent whitespace-nowrap"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">User name / Email</label>
              <input
                type="text"
                value={user.email}
                disabled
                className="w-full px-4 py-2 border rounded-lg bg-muted text-muted-foreground"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-1">Password</label>
              <div className="flex items-center gap-3">
                <input
                  type="password"
                  value="••••••••"
                  disabled
                  className="flex-1 px-4 py-2 border rounded-lg bg-muted text-muted-foreground"
                />
                <button
                  onClick={handleChangePassword}
                  disabled={loading}
                  className="px-4 py-2 border rounded-lg hover:bg-accent disabled:opacity-50 whitespace-nowrap"
                >
                  {loading ? 'Sending...' : 'Change Password'}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                We never store your password. Click the button to receive a secure reset link.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 border-t bg-muted/30 flex justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 border rounded-lg hover:bg-accent"
          >
            Back to Dashboard
          </button>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg hover:opacity-90"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}
